import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  formateKeyName,
  getFormattedData,
  getHiddenKeys,
  getImage,
} from "@/helpers/utils";
import CreativeImagePopupBase from "../modals/CreativeImagePopup";
import StatusChipBase from "../muiCustom/chip/StatusChip";
import TablePaginationBase from "../tables/TablePagination";

type TableChartRow = Record<string, unknown>;

type FormatConfig = {
  prefix: string;
  suffix: string;
};

type TableChartProps = {
  data?: TableChartRow[];
  headings?: string[];
  rows?: number;
  formats?: Record<string, FormatConfig> | null;
  tableFilterKey?: string | number | null;
  hiddenKeys?: unknown;
};

const CreativeImagePopup =
  CreativeImagePopupBase as unknown as React.ComponentType<
    Record<string, unknown> & { children?: React.ReactNode }
  >;
const StatusChip = StatusChipBase as React.ComponentType<
  Record<string, unknown>
>;
const TablePagination = TablePaginationBase as React.ComponentType<
  Record<string, unknown>
>;

const TableChart = ({
  data = [],
  headings = [],
  rows = 6,
  formats = null,
  tableFilterKey = null,
  hiddenKeys = [],
}: TableChartProps) => {
  const location = useLocation();
  const [chartData, setChartData] = useState<TableChartRow[]>([]);
  const hiddenHeadingKeys = getHiddenKeys(hiddenKeys).map((key) => String(key));
  const visibleHeadings = headings.filter(
    (heading) => !hiddenHeadingKeys.includes(heading)
  );

  // pagination
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = rows;
  const getPageDataIndex = () => {
    const from = (currentPage - 1) * pageSize;
    const to = from + pageSize;
    return { from, to };
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    if (tableFilterKey != null) {
      setCurrentPage(1);
    }
  }, [tableFilterKey]);

  useEffect(() => {
    const total = Math.ceil(data.length / pageSize);
    setTotalPage(total);

    setChartData(data.slice(getPageDataIndex().from, getPageDataIndex().to));
  }, [data]);

  useEffect(() => {
    setChartData(data.slice(getPageDataIndex().from, getPageDataIndex().to));
  }, [currentPage]);

  const styles = {
    thead: {
      height: 35,
    },
  };

  const renderStatus = (status: unknown) => {
    const lowerStatus = String(status || "")
      .trim()
      .toLowerCase();

    if (lowerStatus === "scheduled") {
      return (
        <StatusChip
          label="Scheduled"
          status="warn"
          className="location-status"
          showOnlyLabel
        />
      );
    } else if (lowerStatus === "delivered") {
      return (
        <StatusChip
          label="Delivered"
          status={
            location?.pathname?.includes("direct-mail")
              ? "delivered"
              : "arStatus"
          }
          className="location-status"
          showOnlyLabel
        />
      );
    }

    return <span>{String(status ?? "")}</span>;
  };

  return (
    <div className="chart table-cart">
      <div className="table-responsive">
        <table className="table table-borderless table-sm">
          <thead style={styles.thead}>
            <tr className="heading">
              {visibleHeadings.map((h, i) => (
                <th key={i}>
                  <span className="capital fw-sb">{formateKeyName(h)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {chartData.length
              ? chartData.map((d, i) => (
                  <tr key={i}>
                    {visibleHeadings.map((heading) => (
                      <td key={heading}>
                        {(heading === "creative_package" ||
                          heading === "creative_name" ||
                          heading === "hour") &&
                        d?.creative_image ? (
                          <CreativeImagePopup
                            src={d["creative_image"]}
                            srcTwo={d["creative_image_two"]}
                            channelName={d.channel_name || d.Channel}
                            creativeType={d.creative_type}
                            thumbnail={
                              d.thumbnail_image || d.creative_thumbnail
                            }
                            poster={d.creative_poster}
                            heading={getFormattedData(
                              d[heading],
                              heading,
                              formats
                            )}
                          >
                            <span className="link">
                              {getFormattedData(d[heading], heading, formats)}
                            </span>
                          </CreativeImagePopup>
                        ) : heading?.toLowerCase() === "status" ? (
                          renderStatus(
                            getFormattedData(d[heading], heading, formats)
                          )
                        ) : heading === "file" ? (
                          (() => {
                            const fileUrl = d.file;
                            const fileName = d.file_key;

                            return fileUrl && fileName ? (
                              <a
                                href={getImage(
                                  String(fileUrl),
                                  "ASSET_DOWNLOAD_URL"
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link"
                                download
                              >
                                <span
                                  style={{
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {String(fileName).split("/").pop()}
                                </span>
                              </a>
                            ) : (
                              <span>N/A</span>
                            );
                          })()
                        ) : (
                          <span>
                            {getFormattedData(d[heading], heading, formats)}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>
      <div className="container-fluid">
        <div className="row mt-1 align-items-center">
          {/* <div className="col-lg-6 mb-1 mb-lg-0">
            <DataRangeDisplay
              pageIndex={currentPage - 1}
              totalRecords={data?.length || 0}
              pageSize={pageSize}
            />
          </div> */}
          <div className="col-lg float-right">
            <TablePagination
              page={currentPage}
              count={totalPage}
              onChange={handlePageChange}
              className="float-right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableChart;
